import apiConfig from '../config/apiConfig';
import type { BackgroundJob } from './backgroundJobsService';

export type AssistantMode = 'auto' | 'reply' | 'negotiate' | 'practice' | 'deliver' | 'renew' | 'pitch' | 'general' | 'scout';
export interface CreatorProfileSelection { platform: string; handle: string }
/** The quick start a question came from (CONTRACT3 §2). */
export type AssistantStarterKey = 'find' | 'pitch' | 'answer' | 'wrapup';
/**
 * A scout turn's shortlist (CONTRACT3 §3): the run it filed, the channel it scouted, the counts the code wrote its
 * headline from, and the exact rows the saved ProspectRun stores (scout/scoutModel.ts ScoutRec). `considered` is null
 * when the engine didn't report it; `buckets` uses the same thresholds as scoutModel.bucketOf.
 */
export interface ScoutShortlistResult {
  run_id: string;
  platform: string;
  handle: string;
  considered: number | null;
  filed: number;
  buckets: { strong: number; worth: number; stretch: number };
  recommendations: Record<string, unknown>[];
}
/** A chat attached to a saved scout run (CONTRACT3 §9): the rail prints "Brands for my YouTube · 10 brands · 2 questions".
 *  A self-described run has no channel: platform and handle are null (CONTRACT3 CHANGES #8, "Brands for my channel"). */
export interface ThreadScout { run_id: string; platform: string | null; handle: string | null; brand_count: number }
export interface ContextCapability { available: boolean; description: string }
/**
 * One owned channel as GET context-options lists it (CONTRACT3 §5), built by the same selection functions the engine
 * reads with, so a menu row can never promise more than is read. verified = claimed · false = linked by handle (profile
 * only). scoutable = the prospect engine can find brands from its stored data (never decided by the frontend).
 */
export interface ContextChannel {
  platform: string;
  handle: string;
  label: string;
  verified: boolean;
  primary: boolean;
  scoutable: boolean;
  audience: { count: number; unit: 'subscribers' | 'followers' } | null;
  reads: { profile: boolean; topics: boolean; default_videos: number; pickable_videos: number; sponsors: number; cadence: boolean };
}
/** One channel a question reads (CONTRACT3 §2), with its own picks (1..12 videos, 1..20 sponsors; absent = its default). */
export interface ChannelRead extends CreatorProfileSelection { video_ids?: string[]; sponsor_ids?: string[] }
/** 'all' = every owned channel · 'none' = no channel · a list = that subset. Absent from a request = today's single-channel path. */
export type ChannelsSelection = 'all' | 'none' | ChannelRead[];
export interface AssistantContextOptions {
  creator_profiles: CreatorProfileSelection[];
  default_creator_profile: CreatorProfileSelection | null;
  /** v3 (CONTRACT3 §5): every owned channel, in the engine's order. Its presence means the server speaks CONTRACT3. */
  channels?: ContextChannel[];
  capabilities: { creator_profile?: ContextCapability; sponsors?: ContextCapability; performance?: ContextCapability;
    working_brief?: ContextCapability; shared_messages?: ContextCapability; selected_deal?: ContextCapability;
    instagram_inbox?: ContextCapability; tiktok_inbox?: ContextCapability; gmail: ContextCapability & {
    connected: boolean; can_read: boolean | null; live_verified: false; status_basis: 'stored_grant';
    status: 'ready' | 'not_connected' | 'permission_required' | 'scope_unknown' | 'unavailable';
  } };
}
export interface SharedMessage { id: string; platform: 'instagram' | 'tiktok' | 'email' | 'other'; title: string; text: string }
export interface Milestone { id: string; done: boolean; note: string }
export interface AssistantCard { kind: 'advice' | 'draft' | 'comparison' | 'practice' | 'checklist' | 'question'; title: string; body: string; before: string; after: string; items: string[] }
export interface Activity { key: string; label: string; status: 'running' | 'complete' | 'warning'; detail: string; at?: string }
export interface AssistantSource { id: string; title: string; kind: string; truncated?: boolean; creator_profile?: CreatorProfileSelection; provenance?: string }
// ── v5 REPORTS (CONTRACT5 §3 + builder B's CHANGES) ─────────────────────────────────────────────────
/** The three analyses a worksheet runs. Each run uses one report, and only when it delivers. */
export type ReportKind = 'sponsor_shortlist' | 'offer_review' | 'brand_file';
export type OfferFocus = 'usage' | 'exclusivity' | 'payment' | 'deliverables' | 'timeline' | 'legit';
/** What a worksheet sends as `report.inputs`, by kind — exactly the field its source names, nothing else (the server
 *  refuses any other combination). */
export type ReportInputs =
  | { kind: 'sponsor_shortlist'; inputs: { channel: CreatorProfileSelection; anchor_brand?: string } }
  | { kind: 'offer_review'; inputs: { source: 'pasted'; text: string; focus?: OfferFocus[] }
      | { source: 'gmail'; gmail_thread_id: string; focus?: OfferFocus[] }
      | { source: 'deal'; deal_id: string; focus?: OfferFocus[] } }
  | { kind: 'brand_file'; inputs: { brand_id: string } };
/** One offer term, quoted from what the brand wrote (never judged). */
export interface OfferTerm { key: string; label: string; value: string | null; quote: string | null; status: 'stated' | 'missing' | 'unclear';
  source?: string | null; focus?: boolean }
export interface OfferReviewPayload {
  terms: OfferTerm[];
  flags: { key: string; text: string; quote?: string; severity?: string | null; field?: string | null }[];
  questions_to_ask: string[];
  reply_draft: string;
  legit?: { signals: string[]; unknowns: string[] } | null;
  sources?: { kind: string; title: string; subject?: string }[];
  coverage?: { partial?: boolean; snippet_only?: boolean; truncated?: boolean; not_read?: string[] } | null;
  focus?: OfferFocus[];
}
export interface BrandFilePayload {
  brand: { id: string; name: string; category?: string | null; website?: string; logo?: string };
  sponsored: { creator: string; handle?: string | null; platform: string; platform_label?: string; title: string | null; date: string | null; url?: string }[];
  counts: { mentions: number; creators: number; since: string | null };
  creator_sizes: { min: number; median: number; max: number; known?: number } | null;
  formats: string[];
  contact: { status: 'found' | 'withheld' | 'not_found'; email?: string; name?: string; source?: string; note?: string; partnerships_url?: string };
  angles: string[];
  unknowns: string[];
}
/** A report turn's filed document (`result.report`): null when nothing was delivered (then it was free). */
export interface FiledReport {
  id: string;
  kind: ReportKind;
  title: string;
  summary_line: string;
  inputs: Record<string, unknown>;
  payload: Record<string, unknown>;
  charged: boolean;
}
/** One report as its chat lists it (each thread's own `reports`): always filed in that chat. */
export interface ChatReportRow { id: string; kind: ReportKind; title: string; thread_id: string; created_at: string; charged: boolean; summary_line: string }
/** A Reports-shelf row filed in a chat (source 'chat'): it opens that chat. A row with no source is a chat's (an older server).
 *  thread_id null: its chat was deleted — the report stays and opens on its own (GET /reports/<id>). deleted: the creator
 *  deleted it — no title or line, listed only in the month it still counts toward (the plan page shows it; the shelf doesn't). */
export interface ShelfChatRow extends Omit<ChatReportRow, 'thread_id'> { source?: 'chat'; deal_id?: null; thread_id: string | null; deleted?: boolean }
/** A Reports-shelf row for a Deal Room offer read that used a report (source 'deal', C3): an offer review with no chat. It
 *  opens that deal's Offer & terms on that read (/app/deals/<deal_id>?read=<id>); `id` is the read's id. */
export interface ShelfDealRow extends Omit<ChatReportRow, 'kind' | 'thread_id'> { kind: 'offer_review'; source: 'deal'; thread_id: null; deal_id: string; deleted?: false }
/** A filed report as it opens on its own (GET /api/assistant/reports/<id>): the document its turn carried, when it was filed,
 *  and its chat (null once that chat was deleted). */
export interface FiledReportDetail extends FiledReport { created_at: string; thread_id: string | null }
/** One row of the Reports shelf (GET /api/assistant/reports): a chat's report or a deal's offer read, newest first. */
export type ReportShelfRow = ShelfChatRow | ShelfDealRow;

export interface AssistantResult {
  mode?: AssistantMode | 'report';
  /** v5: the report this turn ran (absent on a chat turn). */
  report_kind?: ReportKind | null;
  /** v5: the filed document; null when the run delivered nothing (free). */
  report?: FiledReport | null;
  /** v5: a typed question that is a report's job: Bearri answered free and offers the analysis. */
  suggest_report?: { kind: ReportKind } | null;
  answer?: { headline: string; summary: string; cards: AssistantCard[]; evidence: { source_id: string; quote: string; meaning: string }[]; unknowns: string[]; next_question: string;
    /** v3 (CONTRACT3 §7): ≤3 first-person next questions, filtered server-side of digits, currency and fee words. */
    follow_ups?: string[] };
  activity: Activity[];
  /** v3 (CONTRACT3 §7): whether this turn used an answer. Absent on older turns (then nothing is claimed either way). */
  usage_charged?: boolean;
  /** v3: the quick start echoed back (CONTRACT3 §7). */
  starter?: AssistantStarterKey | null;
  /** v3: a scout turn's shortlist (CONTRACT3 §3). */
  shortlist?: ScoutShortlistResult | null;
  sources?: AssistantSource[];
  coverage?: string[];
  context_revision?: number;
  /** What the engine RECORDED it read: the channel it resolved, and — only on a picked turn — the picks it honoured
   *  (only the fields that were picked). A picked turn run by an engine that predates picks has no `picks`: it read
   *  the default context, so it is never shown as narrowed and never carries into a picked question (CONTRACT §5). */
  context_scope?: {
    creator_profile?: CreatorProfileSelection | null;
    picks?: { video_ids?: string[]; sponsor_ids?: string[]; email_message_ids?: string[] } | null;
    /** v3 (CONTRACT3 §5): every channel the turn read (primary first), with the picks honoured per channel. */
    channels?: { platform: string; handle: string; verified?: boolean; primary?: boolean;
      picks?: { video_ids?: string[]; sponsor_ids?: string[] } | null }[];
    channel_selection?: 'all' | 'none' | 'list';
  };
  edited_drafts?: Record<string, { text: string; revision: number }>;
}
/**
 * What one question read, as the creator chose it — sent with the question (POST /api/jobs, kind 'assistant')
 * and saved on its turn, so a reopened chat restores the last choice. (Backend contract: scratchpad
 * bearri-chat/CONTRACT.md, 2026-09-24.)
 *
 * The three narrowing lists are optional and follow one rule: OMITTED (null on a saved turn) = the engine's
 * default, byte-identical to before (the readable ones of the newest 5 saved videos · the first 10 sponsors
 * on file · the newest 8 messages of the deal's thread when include_email is on); a list = exactly those.
 * `[]` is rejected (422): "back to the default" means leaving the key out. `email_message_ids` goes only with
 * include_email in a deal chat. Caps (validated at enqueue and again when the job runs): video_ids 1..12,
 * sponsor_ids 1..20, email_message_ids 1..8. Unknown keys are rejected. source_ids (pasted messages) ≤6, OFF
 * by default.
 */
export interface AssistantSelection {
  source_ids: string[];
  include_email: boolean;
  creator_profile?: CreatorProfileSelection | null;
  video_ids?: string[] | null;
  sponsor_ids?: string[] | null;
  email_message_ids?: string[] | null;
  /** v3 (CONTRACT3 §2), persisted on the turn's stored request: the quick start, the channel a 'find' scouted, the
   *  "Find more like this" anchor, and whether the chat's shortlist was read. */
  starter?: AssistantStarterKey | null;
  scout_channel?: CreatorProfileSelection | null;
  scout_anchor?: string | null;
  include_shortlist?: boolean;
  /** v3 (CONTRACT3 §2/§5): the channels the question read, with per-channel picks. Absent on a single-channel turn. */
  channels?: ChannelsSelection | null;
  /** v4 (CONTRACT4 §2): the Gmail threads the creator ticked in the composer's Gmail menu (1..3, distinct). Absent = none,
   *  OFF in every new chat; persisted on the turn so a reopened chat restores the ticks. A deal chat's own thread never
   *  rides here (it stays on include_email). */
  gmail_thread_ids?: string[] | null;
  /** v5 (CONTRACT5 §3): a report turn's worksheet inputs, as stored; and whether this chat's reports were read. */
  report?: { kind: ReportKind; inputs: Record<string, unknown> } | null;
  include_reports?: boolean;
}
export interface AssistantTurn extends Omit<BackgroundJob, 'result'>, AssistantSelection { message: string; revision: number; result?: AssistantResult }
export interface ThreadSummary { id: string; title: string; pitch_id: string | null; updated_at: string;
  /** v3 (CONTRACT3 §9): the saved scout run this chat is attached to, its channel and count, and the creator's
   *  questions (turns that are not scout turns). Absent from an older server. */
  scout_run_id?: string | null; scout?: ThreadScout | null; question_count?: number;
  /** v5: this chat's newest report of each kind (what "this chat's report ×" reads). Absent from an older server. */
  reports?: ChatReportRow[] }
export interface AssistantThread extends ThreadSummary { brief: string; shared_messages: SharedMessage[]; milestones: Milestone[]; revision: number; turns: AssistantTurn[]; older_cursor?: string | null }

/**
 * GET /api/assistant/context-catalog?platform=&handle= — what the engine could read from ONE of the
 * creator's own channels (owner-scoped; never touches Gmail; request it only for a channel in
 * context-options' creator_profiles), so the composer's Channel and Sponsors menus can offer real items.
 * `default: true` marks what the engine reads when nothing is picked. Absent (404), refused or failing,
 * the menus fall back to read-only records: never invented rows. An empty channel is 200 with empty lists.
 */
export interface CatalogVideo {
  id: string;
  title: string;
  published_at: string | null;
  views: number | null;
  thumbnail: string | null;
  /** Readable sponsors detected in THIS video ([] when none are linked). */
  sponsors: string[];
  default: boolean;
}
export interface CatalogSponsor {
  id: string;
  name: string;
  logo: string | null;
  /** detected = supported in the channel's own videos · won = its evidence includes a deal reported won in Bearri. */
  origin: 'detected' | 'won';
  /** Stored posts/videos linked as evidence; null when none are linked. */
  mentions: number | null;
  default: boolean;
}
export interface ContextCatalog {
  /** label = the channel's stored name, else "@handle". */
  channel: CreatorProfileSelection & { label: string };
  /** The pickable window: the newest ≤30 saved videos that have a title and views (only those are ever read), newest first. */
  videos: CatalogVideo[];
  /** Every saved row for the channel, readable or not. */
  videos_total: number;
  /** How many videos an unpicked question reads ("Newest {n}"): the real count, ≤5, can be 0. */
  default_video_count: number;
  /** Supported, likely-brand, non-merch sponsors in reading order (capped at 100). */
  sponsors: CatalogSponsor[];
  /** Readable sponsors in all. */
  sponsors_total: number;
  /** How many an unpicked question names: min(10, sponsors_total). Below sponsors_total the default is "Top 10 on file", not "all". */
  default_sponsor_count: number;
}

/**
 * GET /api/assistant/gmail-threads?q= (CONTRACT4 §2) — the creator's own Gmail threads the composer's Gmail menu lists:
 * metadata only (subject, sender, date, count and Gmail's own snippet), never a message body. Always 200 with a status the
 * menu acts on; the listing is read only when the menu opens (or the creator searches), never on its own.
 */
export type GmailThreadsStatus = 'ready' | 'not_connected' | 'permission_required' | 'needs_reconnect' | 'unavailable';
export interface GmailThreadSummary {
  thread_id: string;
  subject: string;
  from: string;
  /** ISO, or null when Gmail gave none. */
  date: string | null;
  message_count: number | null;
  /** Gmail's own snippet (≤140 characters). */
  snippet: string;
  /** A thread Bearri already tracks for this creator (a pitch sent or tracked). */
  tracked: boolean;
}
export interface GmailThreadsResponse { status: GmailThreadsStatus; threads: GmailThreadSummary[]; query: string }

export async function assistantRequest(path: string, token: string, options: RequestInit = {}) {
  const response = await fetch(`${apiConfig.baseUrl}/api/assistant${path}`, {
    ...options, headers: {...options.headers, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
  });
  const data = await response.json();
  if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail
    : typeof data.detail?.message === 'string' ? data.detail.message : 'Bearri could not save that change. Please try again.');
  return data;
}

// ── v5 REPORTS: the request a worksheet's Run sends, and the Reports shelf ─────────────────────────────

/**
 * A report turn's request (POST /api/jobs, kind 'assistant'): the chat, its revision, the creator's note the worksheet
 * wrote, and `report` — nothing else. The server refuses a report that also carries a mode, a starter, channels, picks,
 * pasted sources, email or Gmail threads (a report reads only what its worksheet picked), so none of them is sent.
 * Inputs go exactly as the worksheet built them, empty fields left out.
 */
export function reportPayload({ threadId, revision, message, report }: {
  threadId: string; revision: number; message: string; report: ReportInputs;
}): Record<string, unknown> {
  const inputs = Object.fromEntries(Object.entries(report.inputs as Record<string, unknown>)
    .filter(([, v]) => v !== undefined && v !== null && !(Array.isArray(v) && !v.length) && !(typeof v === 'string' && !v.trim())));
  return { thread_id: threadId, revision, message, report: { kind: report.kind, inputs } };
}

const REPORT_KINDS: ReportKind[] = ['sponsor_shortlist', 'offer_review', 'brand_file'];
/** The most rows GET /api/assistant/reports sends (its own maximum), and what the shelf and the plan page ask for: the
 *  newest 100 of all time, chats' reports and Deal Room reads together. The list isn't paged, so a month with more rows
 *  than that (extras, free rows) lists its newest 100; the months' counts come from the usage summary, never this list. */
export const REPORTS_LIST_MAX = 100;

/**
 * One shelf row as the server sent it, or null when it isn't one (never a row the page has to guess at). A row filed in a
 * chat names its chat; a Deal Room read (source 'deal') is an offer review that names its deal and no chat. A row with no
 * source is a chat's (an older server); a source this page doesn't know is left out (it couldn't be opened).
 */
export function reportRowOf(raw: unknown): ReportShelfRow | null {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object' || typeof r.id !== 'string' || !r.id || !REPORT_KINDS.includes(r.kind as ReportKind)) return null;
  const base = {
    id: r.id,
    title: typeof r.title === 'string' ? r.title : '',
    created_at: typeof r.created_at === 'string' ? r.created_at : '',
    charged: r.charged !== false,
    summary_line: typeof r.summary_line === 'string' ? r.summary_line : '',
  };
  if (r.source === 'deal') {
    if (r.kind !== 'offer_review' || typeof r.deal_id !== 'string' || !r.deal_id) return null;
    return { ...base, kind: 'offer_review', source: 'deal', thread_id: null, deal_id: r.deal_id };
  }
  if (r.source !== undefined && r.source !== null && r.source !== 'chat') return null;
  const deleted = r.deleted === true;
  // deleted by the creator and it never counted: there's nothing to open and nothing it still counts toward, so it
  // isn't a row anywhere (the shelf hides every deleted report; the plan page lists the ones that still count)
  if (deleted && !base.charged) return null;
  // its chat was deleted: the report stays, with no chat (only ever said as null — a missing chat is not a row)
  if (r.thread_id === null) return { ...base, kind: r.kind as ReportKind, source: 'chat', thread_id: null, deal_id: null, deleted };
  if (typeof r.thread_id !== 'string' || !r.thread_id) return null;
  return { ...base, kind: r.kind as ReportKind, source: 'chat', thread_id: r.thread_id, deal_id: null, deleted };
}

/** A report request the server refused, with its status: 404 (gone: deleted, maybe in another tab) and 409 (a delete of a
 *  report that is in a chat again) each mean something the sheet acts on. `message` is the server's own sentence, for
 *  logs only: the sheet says its own words. */
export class ReportRequestError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message || `Report request failed (${status}).`);
    this.name = 'ReportRequestError';
    this.status = status;
  }
}

/** One report, read or deleted (owner-only). A refusal throws ReportRequestError with its status; a dropped connection
 *  throws what fetch threw. */
async function reportRequest(token: string, id: string, method: 'GET' | 'DELETE') {
  const response = await fetch(`${apiConfig.baseUrl}/api/assistant/reports/${encodeURIComponent(id)}`, {
    method, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new ReportRequestError(response.status, typeof data?.detail === 'string' ? data.detail : '');
  return data;
}

/** GET /api/assistant/reports/<id> — one of the creator's own reports, the document itself (owner-only, no-store), and
 *  its chat (null while it has none: a report goes back into a chat when its saved shortlist is opened again). Throws
 *  ReportRequestError 404 for a deleted report or anyone else's, and a plain Error for a reply that isn't a report. */
export async function fetchReport(token: string, id: string): Promise<FiledReportDetail> {
  const d = await reportRequest(token, id, 'GET');
  if (!d || typeof d.id !== 'string' || !REPORT_KINDS.includes(d.kind) || !d.payload || typeof d.payload !== 'object') {
    throw new Error('This report could not be read.');
  }
  return { id: d.id, kind: d.kind, title: typeof d.title === 'string' ? d.title : '', summary_line: typeof d.summary_line === 'string' ? d.summary_line : '',
    inputs: d.inputs && typeof d.inputs === 'object' ? d.inputs : {}, payload: d.payload, charged: d.charged !== false,
    created_at: typeof d.created_at === 'string' ? d.created_at : '', thread_id: typeof d.thread_id === 'string' && d.thread_id ? d.thread_id : null };
}

/** DELETE /api/assistant/reports/<id> — a report whose chat was deleted. It still counts toward its month (never refunded):
 *  `stillCounts` is the server's word (false only for a report that never counted, which the server removes outright).
 *  Throws ReportRequestError 409 when the report is in a chat again, 404 when it is already gone. */
export async function deleteReport(token: string, id: string): Promise<{ stillCounts: boolean }> {
  const d = await reportRequest(token, id, 'DELETE');
  return { stillCounts: d?.still_counts !== false };
}

/** GET /api/assistant/reports?limit= — the creator's own reports, newest first (owner-only, no-store): the chats' reports
 *  and the Deal Room reads that used one. Throws when the shelf can't be read (the rail says so with Retry, never
 *  "nothing filed"). */
export async function fetchReports(token: string, limit = REPORTS_LIST_MAX): Promise<ReportShelfRow[]> {
  const data = await assistantRequest(`/reports?limit=${Math.max(1, Math.min(REPORTS_LIST_MAX, Math.round(limit)))}`, token);
  const rows = Array.isArray(data?.reports) ? data.reports : null;
  if (!rows) throw new Error('The reports list could not be read.');
  return rows.map(reportRowOf).filter((r: ReportShelfRow | null): r is ReportShelfRow => !!r);
}
